import * as Sentry from '@sentry/react';
import config from '../config';

const SENTRY_REPORTED = Symbol('sentryReported');

const SENSITIVE_KEYS = [
    'name',
    'first_name',
    'last_name',
    'email',
    'phone',
    'mobile',
    'address',
    'token',
    'access',
    'refresh',
    'authorization',
    'password',
    'patient',
    'patient_id',
    'patient_name',
    'report',
    'report_id',
    'image',
    'file',
    'url',
    'presigned_url',
    'upload_url',
    'abha',
    'other_id',
    'signature',
    'credential',
];

function isAxiosError(error) {
    return !!(error && (error.isAxiosError || error.response || error.config));
}

function getRequestUrl(error) {
    return error?.config?.url || error?.request?.responseURL || '';
}

function isNetworkError(error) {
    return (
        !error?.response &&
        (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error')
    );
}

/** Strip query params and fragments (tokens, presigned URLs, signatures). */
export function sanitizeUrl(url) {
    if (!url) return '';

    try {
        const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return `${parsed.origin}${parsed.pathname}`;
    } catch {
        return String(url).split('?')[0].split('#')[0];
    }
}

function truncateValue(value, maxLen = 500) {
    if (value == null) return value;
    if (typeof value === 'string') {
        return value.length > maxLen ? `${value.slice(0, maxLen)}…` : value;
    }
    try {
        const s = JSON.stringify(value);
        return s.length > maxLen ? `${s.slice(0, maxLen)}…` : value;
    } catch {
        return String(value);
    }
}

function redactObject(obj, depth = 0) {
    if (obj == null) return obj;
    if (depth > 3) return '[TRUNCATED]';

    if (Array.isArray(obj)) {
        return obj.slice(0, 10).map((item) => redactObject(item, depth + 1));
    }

    if (typeof obj !== 'object') {
        return truncateValue(obj, 300);
    }

    const out = {};
    let n = 0;
    for (const [key, value] of Object.entries(obj)) {
        if (n >= 20) break;
        const lower = key.toLowerCase();

        if (SENSITIVE_KEYS.some((k) => lower.includes(k))) {
            out[key] = '[REDACTED]';
        } else if (value != null && typeof value === 'object') {
            out[key] = redactObject(value, depth + 1);
        } else {
            out[key] = truncateValue(value, 300);
        }
        n += 1;
    }
    return out;
}

/** Skip expected offline/network noise; workflow catches own 4xx and most failures. */
function shouldSkipAxiosReport(error) {
    const url = getRequestUrl(error);
    const isNetwork = isNetworkError(error);
    const isOffline =
        typeof navigator !== 'undefined' && navigator.onLine === false;

    if (isNetwork && isOffline) return true;

    if (url.includes('/api/health/')) {
        const isTimeout =
            error.code === 'ECONNABORTED' ||
            (error.message && /timeout/i.test(error.message));
        if (isTimeout || isNetwork) return true;
    }

    return false;
}

function addSkippedNetworkBreadcrumb(error, context = {}) {
    const { tags = {} } = context;
    Sentry.addBreadcrumb({
        category: 'network',
        message: 'Request failed (network, not reported as exception)',
        level: 'warning',
        data: redactObject({
            url: sanitizeUrl(getRequestUrl(error)),
            method: error.config?.method,
            errorCode: error.code,
            ...tags,
        }),
    });
}

function buildAxiosExtras(error) {
    if (!isAxiosError(error)) return {};
    return {
        url: sanitizeUrl(getRequestUrl(error)),
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorCode: error.code,
        responseData: redactObject(error.response?.data),
    };
}

/**
 * Report an error to Sentry once per Error instance (avoids duplicate axios + catch noise).
 *
 * @param {unknown} error
 * @param {{ tags?: Record<string, unknown>, extra?: Record<string, unknown>, level?: 'error'|'warning'|'info', fingerprint?: string[] }} [context]
 */
export function captureAppError(error, context = {}) {
    if (!config.SENTRY_URL || !error) return;

    const err = error instanceof Error ? error : new Error(String(error));

    if (err[SENTRY_REPORTED]) {
        const { tags = {}, extra = {} } = context;
        if (Object.keys(tags).length || Object.keys(extra).length) {
            Sentry.addBreadcrumb({
                category: 'app.error.context',
                message: String(tags.stage || tags.errorType || 'additional_context'),
                level: context.level === 'warning' ? 'warning' : 'error',
                data: redactObject({ ...tags, ...extra }),
            });
        }
        return;
    }

    if (isAxiosError(err) && shouldSkipAxiosReport(err)) {
        if (isNetworkError(err)) {
            addSkippedNetworkBreadcrumb(err, context);
        }
        return;
    }

    err[SENTRY_REPORTED] = true;

    const { tags = {}, extra = {}, level = 'error', fingerprint } = context;

    Sentry.withScope((scope) => {
        scope.setLevel(level);
        Object.entries(tags).forEach(([k, v]) => {
            if (v != null) scope.setTag(k, String(v));
        });
        const safeExtra = redactObject(extra);
        scope.setExtras({
            ...buildAxiosExtras(err),
            ...safeExtra,
        });
        if (fingerprint?.length) {
            scope.setFingerprint(fingerprint);
        }
        Sentry.captureException(err);
    });
}

/** Global axios interceptor: only unhandled server errors (local catches own 4xx/network). */
export function shouldCaptureInGlobalAxiosInterceptor(error) {
    if (!error || shouldSkipAxiosReport(error) || isAuthRedirectAxiosError(error)) {
        return false;
    }
    const status = error.response?.status;
    return typeof status === 'number' && status >= 500;
}

/** True when axios interceptor should redirect to login. */
export function isAuthRedirectAxiosError(error) {
    const status = error?.response?.status;
    if (status !== 401 && status !== 403) return false;
    const url = getRequestUrl(error);
    return (
        !url.includes('/model') &&
        !url.includes('onnx') &&
        !url.includes('wasm')
    );
}

/** Auth expiry is expected — breadcrumb only, not an exception. */
export function recordAuthRedirectBreadcrumb(error) {
    if (!config.SENTRY_URL || !error) return;

    Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Auth redirect due to 401/403',
        level: 'warning',
        data: {
            status: error.response?.status,
            url: sanitizeUrl(getRequestUrl(error)),
        },
    });
}
